import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { enablePush } from "@/lib/push";
import { toast } from "sonner";
import { Plus, MessageSquare, Eye, EyeOff, User, LogOut, LayoutDashboard, Heart, Search, Euro, ShieldAlert, Languages, Gavel, PackageSearch, Bell, BarChart3, Sparkles, Users } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();

  const handleEnablePush = async () => {
    try {
      await enablePush();
      toast.success("Meldingen staan aan! Je krijgt nu pushberichten op dit apparaat.");
    } catch (err) {
      toast.error(err.message || "Kon meldingen niet aanzetten");
    }
  };
  const [discreet, setDiscreet] = useState(() => localStorage.getItem("velvet_discreet") === "true");
  const [notif, setNotif] = useState({ pending_bids: 0, unread_messages: 0 });

  useEffect(() => {
    if (!user) { setNotif({ pending_bids: 0, unread_messages: 0 }); return; }
    let active = true;
    const fetchNotif = () => api.get("/notifications").then(({ data }) => { if (active) setNotif(data); }).catch(() => {});
    fetchNotif();
    const iv = setInterval(fetchNotif, 30000);
    return () => { active = false; clearInterval(iv); };
  }, [user]);

  useEffect(() => {
    localStorage.setItem("velvet_discreet", discreet);
    document.title = discreet ? "Sfeerverlichting & Lifestyle" : "Fetshop.nl — Discrete Marktplaats";
    if (discreet) document.body.classList.add("discreet-mode");
    else document.body.classList.remove("discreet-mode");
  }, [discreet]);

  return (
    <header className="sticky top-0 z-50 border-b border-zinc-800/60 bg-zinc-950/80 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-3 sm:px-6 flex h-16 items-center justify-between gap-2 sm:gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          <span className="font-serif text-2xl font-semibold tracking-tight text-zinc-100">FETSHOP</span>
          <span className="hidden sm:inline font-mono text-[10px] uppercase tracking-[0.25em] text-rose-400 mt-1">.nl</span>
        </Link>

        <div className="flex items-center gap-1.5 sm:gap-3">
          <Link
            to="/diensten"
            data-testid="nav-services-link"
            className="flex items-center gap-1.5 rounded-lg border border-rose-800/50 bg-rose-950/30 px-2 sm:px-3 py-2 text-xs font-medium text-rose-300 hover:text-rose-100 hover:bg-rose-950/60 transition-colors"
          >
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">Diensten</span>
            <span className="hidden lg:inline rounded-full bg-rose-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">Nieuw</span>
          </Link>
          <button
            data-testid="language-toggle"
            onClick={() => setLang(lang === "nl" ? "en" : "nl")}
            title="Taal / Language"
            className={`${user ? "hidden sm:flex" : "flex"} items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 transition-colors`}
          >
            <Languages className="h-4 w-4" />
            <span className="hidden md:inline uppercase">{lang}</span>
          </button>
          <button
            data-testid="discreet-mode-toggle"
            onClick={() => setDiscreet((d) => !d)}
            title="Discrete modus"
            className="flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-900/60 px-2 sm:px-3 py-2 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 transition-colors"
          >
            {discreet ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            <span className="hidden md:inline">{discreet ? t("nav.discreet_on") : t("nav.discreet_off")}</span>
          </button>

          <button
            data-testid="nav-search-button"
            onClick={() => navigate("/")}
            className="hidden rounded-lg border border-zinc-800 bg-zinc-900/60 p-2 text-zinc-400"
          >
            <Search className="h-4 w-4" />
          </button>

          {user ? (
            <>
              <Link
                to="/plaats"
                data-testid="nav-create-listing-button"
                className="flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-3 sm:px-4 py-2 text-sm shadow-lg shadow-rose-950/50 transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">{t("nav.place")}</span>
              </Link>
              <button
                data-testid="nav-bids-bell"
                onClick={() => navigate("/dashboard")}
                title="Nieuwe biedingen"
                className="relative rounded-lg border border-zinc-800 bg-zinc-900/60 p-2 text-zinc-400 hover:text-rose-400 transition-colors"
              >
                <Bell className="h-4 w-4" />
                {notif.pending_bids > 0 && (
                  <span data-testid="bids-notif-badge" className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
                    {notif.pending_bids > 9 ? "9+" : notif.pending_bids}
                  </span>
                )}
              </button>
              <Link to="/berichten" data-testid="nav-messages-button" className="relative rounded-lg border border-zinc-800 bg-zinc-900/60 p-2 text-zinc-400 hover:text-rose-400 transition-colors">
                <MessageSquare className="h-4 w-4" />
                {notif.unread_messages > 0 && (
                  <span data-testid="messages-notif-badge" className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
                    {notif.unread_messages > 9 ? "9+" : notif.unread_messages}
                  </span>
                )}
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-trigger" className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-950/50 border border-rose-800/40 text-rose-300 text-sm font-medium">
                    {(user.name || "U").charAt(0).toUpperCase()}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-zinc-950 border-zinc-800 text-zinc-200 w-52">
                  <div className="px-2 py-1.5 text-xs text-zinc-500 truncate">{user.email}</div>
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")} className="cursor-pointer focus:bg-zinc-800">
                    <LayoutDashboard className="h-4 w-4 mr-2" /> {t("menu.dashboard")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-favorites" onClick={() => navigate("/favorieten")} className="cursor-pointer focus:bg-zinc-800">
                    <Heart className="h-4 w-4 mr-2" /> {t("menu.favorites")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-bids" onClick={() => navigate("/mijn-biedingen")} className="cursor-pointer focus:bg-zinc-800">
                    <Gavel className="h-4 w-4 mr-2" /> {t("menu.bids")}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-messages" onClick={() => navigate("/berichten")} className="cursor-pointer focus:bg-zinc-800">
                    <MessageSquare className="h-4 w-4 mr-2" /> {t("menu.messages")}
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-revenue" onClick={() => navigate("/omzet")} className="cursor-pointer focus:bg-zinc-800 text-rose-300">
                      <Euro className="h-4 w-4 mr-2" /> {t("menu.revenue")}
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-analytics" onClick={() => navigate("/bezoekers")} className="cursor-pointer focus:bg-zinc-800 text-rose-300">
                      <BarChart3 className="h-4 w-4 mr-2" /> Bezoekers
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="menu-language" onClick={() => setLang(lang === "nl" ? "en" : "nl")} className="sm:hidden cursor-pointer focus:bg-zinc-800">
                    <Languages className="h-4 w-4 mr-2" /> {lang === "nl" ? "English" : "Nederlands"}
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-enable-push" onClick={handleEnablePush} className="cursor-pointer focus:bg-zinc-800">
                    <Bell className="h-4 w-4 mr-2" /> Meldingen aanzetten
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin-products" onClick={() => navigate("/advertenties")} className="cursor-pointer focus:bg-zinc-800 text-rose-300">
                      <PackageSearch className="h-4 w-4 mr-2" /> Advertenties
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin-users" onClick={() => navigate("/gebruikers")} className="cursor-pointer focus:bg-zinc-800 text-rose-300">
                      <Users className="h-4 w-4 mr-2" /> Gebruikers
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-moderation" onClick={() => navigate("/moderatie")} className="cursor-pointer focus:bg-zinc-800 text-rose-300">
                      <ShieldAlert className="h-4 w-4 mr-2" /> {t("menu.moderation")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="cursor-pointer focus:bg-zinc-800 text-rose-400">
                    <LogOut className="h-4 w-4 mr-2" /> {t("menu.logout")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <Link to="/auth" data-testid="nav-login-button" className="flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-medium px-4 py-2 text-sm transition-all hover:scale-[1.02]">
              <User className="h-4 w-4" /> {t("nav.login")}
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
